import { useAuth } from '@cmg/auth';
import type { SectionProps } from '@cmg/design-system';
import { Alert, Section, SectionContentSkeleton, Stack, Typography } from '@cmg/design-system';
import { FieldArray } from 'formik';
import isEmpty from 'lodash/isEmpty';
import React from 'react';

import { InvestorContactAlertBanner } from '../alert-banner/InvestorContactAlertBanner';
import InvestorContactSearch from '../contact-search/InvestorContactSearch';
import InvestorContactsTable from '../contacts-table/InvestorContactsTable';
import { useInvestorContactInformation_UnderwriterQuery } from '../graphql/__generated__';
import { InvestorContact } from '../InvestorContactInformationForm.model';

export type Props = {
  buySideContacts: InvestorContact[];
  sellSideContacts: InvestorContact[];
  creating?: boolean;
  bankInvestorName?: string | null;
  bankInvestorKey?: string | null;
  loading?: boolean;
  saving?: boolean;
  isCoveredAccount: boolean;
  investorContactMandatory: boolean;
  actions?: NonNullable<SectionProps['actions']>;
  editing?: boolean;
};

const InvestorContactInformation: React.FC<Props> = ({
  creating,
  buySideContacts,
  sellSideContacts,
  bankInvestorKey,
  bankInvestorName,
  loading,
  saving,
  isCoveredAccount,
  investorContactMandatory,
  actions,
  editing,
}) => {
  const { oidcUserCmgEntityKey } = useAuth();
  const {
    data,
    loading: queryLoading,
    error,
  } = useInvestorContactInformation_UnderwriterQuery({
    variables: { cmgEntityKey: oidcUserCmgEntityKey! },
    skip: !oidcUserCmgEntityKey || isCoveredAccount,
  });
  // eslint-disable-next-line
  // TODO: user's firm name should be part of auth context
  const isInEdit = !!creating || !!editing;

  const isLoading = queryLoading || loading;

  return (
    <Section id="contact-information" title="Investor Contacts" actions={actions}>
      {isLoading && <SectionContentSkeleton />}
      {!isLoading && (
        <Stack>
          <Stack gap={2}>
            {error && <Alert severity="error">{error.message}</Alert>}
            {editing && <InvestorContactAlertBanner />}
            <FieldArray name="sellSideContacts">
              {({ unshift, remove }) => (
                <Stack>
                  {isInEdit && (
                    <InvestorContactSearch
                      handleAddContact={unshift}
                      bankInvestorKey={bankInvestorKey ?? undefined}
                      disabled={saving || loading}
                      isCoveredAccount={isCoveredAccount}
                      existingContacts={[...buySideContacts, ...sellSideContacts]}
                    />
                  )}
                  <InvestorContactsTable
                    buySideContacts={buySideContacts}
                    sellSideContacts={sellSideContacts}
                    editing={isInEdit}
                    handleRemoveContact={remove}
                    bankInvestorName={bankInvestorName}
                    usersFirmName={data?.underwriter.displayName}
                    contactsMandatory={investorContactMandatory && isCoveredAccount} // form cannot be sent without contacts only for S&T users
                  />
                </Stack>
              )}
            </FieldArray>
          </Stack>
          {isEmpty(buySideContacts) && isEmpty(sellSideContacts) && (
            <Stack py={3} px={0}>
              <Typography variant="body1" color="text.secondary">
                No Investor Contacts Added
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
    </Section>
  );
};

export default InvestorContactInformation;
